import MenuItem from "@mui/material/MenuItem";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  API_URL_STATUS,
  API_URL_COLUMNS,
  API_BASE_URL,
  dateTimeFormatter,
} from "./Configuration";
import {
  applySavedColumnLayout,
  getColumnLayoutScope,
} from '../integrations/core/table/columnLayoutPersistence';
import NumericCellEditor from '../components/custom_column/NumericCellEditor';
//import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
//ModuleRegistry.registerModules([ SetFilterModule ]);

// get array of column names and put them into ag grid definition schema
export const enrichTableColumns = (data: { columns: any[] }) => {
  const tableColumns = [];
  if (data.columns) {
    data.columns.forEach((value) => {
      tableColumns.push({
        field: value,
        headerName: value,
        editable: false,
        resizable: true,
        filter: true,
      });
    });
  }
  return tableColumns;
};

// transform list into drowndown selection with menu items
export function menuItems(list: any[]) {
  return list.map((column, index) => (
    <MenuItem value={column} key={index}>
      {column}
    </MenuItem>
  ));
}
export function menuItemsDict(dict) {
  // Sort keys by their corresponding values alphabetically (case-insensitive)
  const sortedKeys = Object.keys(dict).sort((a, b) => 
    dict[a].toLowerCase().localeCompare(dict[b].toLowerCase())
  );
  
  return sortedKeys.map((key) => (
    <MenuItem value={key} key={key}>
      {dict[key]}
    </MenuItem>
  ));
}

// check for upload or posting statusH
export const isPosted = (state) => {
  return state; //TODO: inline this "identity operator"?
};

// Extract filename from Content-Disposition header
export const extractFilenameFromContentDisposition = (contentDisposition: string): string => {
  let filename = "export.xlsx"; // fallback
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }
  return filename;
};

interface DepartmentStatusRow {
  department: string;
  viewed: boolean;
  declined: boolean;
  decline_reason: string;
  document_uploaded: boolean;
  prices_submitted: boolean;
}

// Build and download the Department Status popup's rows as an Excel file
export const exportDepartmentStatusToExcel = (
  tenderId: string | number,
  rows: DepartmentStatusRow[]
) => {
  const yesNo = (value: boolean) => (value ? "Yes" : "No");
  const sheetRows = rows.map((row) => ({
    Department: row.department,
    Viewed: yesNo(row.viewed),
    Declined: yesNo(row.declined),
    "Decline Reason": row.declined ? row.decline_reason : "",
    "Document Uploaded": yesNo(row.document_uploaded),
    "Prices Submitted": yesNo(row.prices_submitted),
  }));

  const worksheet = XLSX.utils.json_to_sheet(sheetRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Department Status");
  XLSX.writeFile(workbook, `Department_Status_Tender_${tenderId}.xlsx`);
};

interface MissedTender {
  tender_id: number;
  company: string | null;
  tender_status: string;
  sent_at: string;
  internal_deadline: string | null;
}

interface DepartmentInactivityRow {
  department: string;
  received: number;
  reacted: number;
  missed: number;
  pending: number;
  response_rate: number | null;
  last_reaction_at: string | null;
  missed_tenders: MissedTender[];
}

// Build and download the Analytics page's department activity as an Excel file
export const exportDepartmentInactivityToExcel = (
  rows: DepartmentInactivityRow[],
  start: string,
  end: string
) => {
  const summaryRows = rows.map((row) => ({
    Department: row.department,
    Received: row.received,
    Reacted: row.reacted,
    Missed: row.missed,
    Pending: row.pending,
    "Response Rate (%)":
      row.response_rate === null ? "" : Math.round(row.response_rate * 100),
    "Last Reaction": dateTimeFormatter({ value: row.last_reaction_at ?? "" }),
  }));
  const missedRows = rows.flatMap((row) =>
    row.missed_tenders.map((tender) => ({
      Department: row.department,
      "Tender ID": tender.tender_id,
      Company: tender.company ?? "",
      Status: tender.tender_status,
      Sent: dateTimeFormatter({ value: tender.sent_at }),
      "Internal Deadline": dateTimeFormatter({
        value: tender.internal_deadline ?? "",
      }),
    }))
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(summaryRows),
    "Department Activity"
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(missedRows),
    "Missed Tenders"
  );
  XLSX.writeFile(workbook, `Department_Activity_${start}_${end}.xlsx`);
};

function currencyFormatter(currency, sign) {
  if (currency !== null) {
    if (typeof currency !== "number") {
      return currency;
    } else if (currency === -3141) {
      return `X`;
    } else {
      return currency.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + sign;
    }
  } else {
    return currency;
  }
}

function currencyFormatterInt(currency, sign) {
  if (currency !== null) {
    if (typeof currency !== "number") {
      return currency;
    } else if (currency === -3141) {
      return `X`;
    } else {
      return currency.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + sign;
    }
  } else {
    return currency;
  }
}

function distanceFormatter(percent, sign) {
  if (percent !== null) {
    if (typeof percent !== "number") {
      return percent;
    } else {
      return percent.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + sign;
    }
  } else {
    return percent;
  }
}
function percentFormatter(percent, sign) {
  if (percent !== null) {
    if (typeof percent !== "number") {
      return percent;
    } else {
      return percent.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + sign;
    }
  } else {
    return percent;
  }
}

const filterOptionsPrice = {
  suppressAndOrCondition: true,
  filterOptions: [
    "empty",
    "greaterThan",
    "lessThan",
    "inRange",
    {
      displayKey: "Not entered",
      displayName: "Not entered",
      predicate: (_, cellValue) => cellValue === null || cellValue === -3141,
      numberOfInputs: 0,
    },
  ],
};

const fetchMap = async (id) => {
  await axios.get(`${API_BASE_URL}/api/polyline/${id}`).then((res) => {
    const polyline = res.data.polyline;
    var event = new CustomEvent("polyLineCreated", {
      detail: polyline,
    });
    document.dispatchEvent(event);
  });
};

export async function headerColumns(tenderID, tenderDataOrOverview, overview = false, isViewer = false) {
  const tenderData = typeof tenderDataOrOverview === 'boolean'
    ? undefined
    : tenderDataOrOverview;
  const isOverview = typeof tenderDataOrOverview === 'boolean'
    ? tenderDataOrOverview
    : overview;
  let tempUser = localStorage.getItem("user");

  // Use the new departments-for-tender endpoint if we have a tender ID
  let departments;
  try {
    departments = (
      await axios.get(`${API_BASE_URL}/api/departments-for-tender/${tenderID}/${tempUser}`)
    ).data;
  } catch (error) {
    // Fallback to regular departments endpoint
    console.warn("Could not fetch departments for tender, using fallback:", error);
    departments = (
      await axios.get(`${API_BASE_URL}/api/departments/${tempUser}`)
    ).data;
  }

  let columns = (await axios.get(API_URL_COLUMNS + tenderID)).data;
  let statusH = (await axios.get(API_URL_STATUS + tenderID)).data;

  let tender = (
    await axios.get(`${API_BASE_URL}/api/tenders_lanes_view/${tenderID}`)
  ).data;

  const userData = (await axios.get(`${API_BASE_URL}/api/is-admin/${tempUser}`))
    .data;

  const customColumns = (
    await axios.get(`${API_BASE_URL}/api/tender/custom-columns/${tenderID}`)
  ).data;

  // Fetch department view status
  let departmentViews = {};
  try {
    departmentViews = (
      await axios.get(
        `${API_BASE_URL}/api/tender/${tenderID}/department-views/`
      )
    ).data;
  } catch (error) {
    console.warn("Could not fetch department views:", error);
  }

  const isAdmin = userData.is_admin;
  const isStaff = userData.is_staff;
  const isRegular = userData.is_regular;
  

  
  let header: any[] = [
    {
      field: "originCountry",
      headerName: "Origin\nCountry",
      // filter: true,
      filter: "agSetColumnFilter",
      filterParams: {
        // can be 'windows' or 'mac'
        excelMode: "windows",
      },
      resizable: true,
      sortable: true,
    },
  ];
  if (columns.includes("originCity")) {
    header.push({
      field: "originCity",
      headerName: "Origin\nCity",
      filter: true,
      resizable: true,
      sortable: true,
    });
    columns.splice(columns.indexOf("originCity"), 1);
  }
  header.push({
    field: "originZIP",
    headerName: "Origin\nZIP",
    filter: true,
    resizable: true,
    sortable: true,
    cellRenderer: (params) => {
      if (params.data?.origin_no_matching) {
        return <span style={{ color: '#ed6c02', fontWeight: 500 }}>⚠ {params.value || 'No matching'}</span>;
      }
      return params.value ?? '';
    },
  });
  if (columns.includes("destinationCity")) {
    header.push({
      field: "destinationCity",
      headerName: "Destination\nCity",
      filter: true,
      resizable: true,
      sortable: true,
    });
    columns.splice(columns.indexOf("destinationCity"), 1);
  }
  if (columns.includes("palletExchange")) {
    header.push({
      field: "palletExchange",
      headerName: "Pallet\nExchange",
      filter: true,
      width: 140,
      resizable: true,
      sortable: true,
    });
    columns.splice(columns.indexOf("palletExchange"), 1);
  }

  header.push(
    {
      field: "destinationCountry",
      headerName: "Destination\nCountry",
      filter: true,
      resizable: true,
      sortable: true,
    },
    {
      field: "destinationZIP",
      headerName: "Destination\nZIP",
      filter: true,
      resizable: true,
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.destination_no_matching) {
          return <span style={{ color: '#ed6c02', fontWeight: 500 }}>⚠ {params.value || 'No matching'}</span>;
        }
        return params.value ?? '';
      },
    },
    {
      field: "volume",
      headerName: "Volume",
      sortable: true,
      width: 140,
      resizable: true,
      filter: true,
      filterParams: filterOptionsPrice,
    },
    {
      field: "volumeUnit",
      headerName: "Volume Unit",
      sortable: true,
      width: 140,
      resizable: true,
      filter: true,
    }
    //{ field: 'palletExchange', headerName: 'Pallet Exchange', sortable: true, width: 140,resizable:true, filter: true },
  );

  let headerDict = {
    equipment: "Equipment",
    payload: "Payload",
    transitTime: "Transit Time",
    requirements: "Special Requirements",
    registerSheet: "Register Sheet",
    oneway: "Oneway / Roundtrip",
    customerLaneID: "Customer Lane ID",
  };

  // Custom formatter for oneway field to show pricing multiplier
  const onewayFormatter = (params) => {
    const value = params.value;
    if (!value || value === "- no Mapping -") return value;

    const multipliers = {
      oneway: "1.0x",
      roundtrip: "2.0x",
    };

    const multiplier = multipliers[value.toLowerCase()];
    return multiplier ? `${value} (${multiplier})` : value;
  };

  // Define which columns are lane properties (only editable by admins)
  const lanePropertyFields = ['equipment', 'payload', 'transitTime', 'requirements', 'registerSheet', 'oneway', 'customerLaneID'];

  columns.forEach((value) => {
    const columnDef: any = {
      field: value,
      headerName: headerDict[value],
      sortable: true,
      filter: true,
      resizable: true,
    };

    // Only allow admins to edit lane property fields
    if (lanePropertyFields.includes(value)) {
      columnDef.editable = isAdmin;
    }

    if (value === "oneway") {
      columnDef.valueFormatter = onewayFormatter;
      if (isAdmin) {
        columnDef.editable = true;
        columnDef.cellEditor = "agSelectCellEditor";
        columnDef.cellEditorParams = {
          values: ["- no Mapping -", "oneway", "roundtrip"],
        };
      }
    }
    header.push(columnDef);
  });

  if (customColumns.length > 0) {
    customColumns
      .map((column) => {
        // Handle both old string format and new object format
        const columnName = typeof column === 'string' ? column : column.name;
        const isRequired = typeof column === 'string' ? false : (column.required || false);
        // Use column_ref (full backend key like "custom_0_Foo") for the field so it matches
        // the "_mapped" data key the backend produces. Fall back to display name for legacy entries.
        const columnRef = typeof column === 'string' ? column : (column.column_ref || column.name);

        // Input columns get their own department-specific field below. Do not also
        // add their global mapped value as an Information column.
        if (isRequired) {
          return undefined;
        }

        // Clean header name: remove "custom_" prefix and numeric index like "0_", "1_", etc.
        const cleanHeaderName = columnName.replace("custom_", "").replace(/^\d+_/, "");
        
        // The backend adds "_mapped" suffix to ALL global custom columns from LaneCustomColumns
        // to prevent collision with department-specific columns during pandas merge
        const fieldName = `${columnRef}_mapped`;
        
        const customColumnDef: any = {
          field: fieldName,
          headerName: cleanHeaderName,
          filter: true,
          resizable: true,
          width: 140,
          sortable: true,
        };
        
        // For admin users, all custom columns are editable
        if (isAdmin) {
          customColumnDef.editable = true;
        } else {
          // For department/viewer users, non-required (Information) custom columns are read-only
          customColumnDef.editable = false;
        }
      
      return header.push(customColumnDef);
    });
  }
  // For department users, show informational capacity column here, grouped with read-only reference data
  if (!isAdmin && !isViewer && tender?.[0]?.info_capacity) {
    const infoCapText = (tenderData?.capacityText || '').trim().toLowerCase();
    const infoCapLabel = infoCapText === 'percentage' ? 'Capacity (%)' :
      ['daily', 'weekly', 'monthly', 'yearly'].includes(infoCapText) ? `Capacity (${infoCapText})` : 'Capacity';
    if (tender?.[0]?.capacity_required) {
      // Both editable + info: the read-only reference goes here; editable stays near regional price
      header.push({
        field: "info_capacity",
        headerName: `${infoCapLabel} (info)`,
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: false,
        width: 140,
      });
    } else {
      // Info-only (not required to fill): single read-only capacity column goes here
      header.push({
        field: "capacity",
        headerName: infoCapLabel,
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: false,
        width: 120,
      });
    }
  }
  header.push(
    {
      field: "distance_here",
      headerName: "Distance",
      valueFormatter: (params) => distanceFormatter(params.value, "km"),
      filter: true,
      resizable: true,
      width: 140,

      filterParams: filterOptionsPrice,
      sortable: true,
    },
    {
      field: "tolls",
      headerName: "Tolls",
      width: 140,
      valueFormatter: (params) => currencyFormatter(params.value, "€"),
      filter: true,
      resizable: true,
      filterParams: filterOptionsPrice,
      sortable: true,
    }
  );
  if (statusH === "3") {
    header.push(
      {
        field: "round2",
        width: 140,
        headerName: "Price\nFinal Round 2",
        valueFormatter: (params) => currencyFormatter(params.value, "€"),
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
      },
      {
        field: "customer_abs3",
        width: 140,
        headerName: "Price\nCustomer",
        valueFormatter: (params) => currencyFormatter(params.value, "€"),
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
      }
    );
  } else if (statusH === "2") {
    header.push(
      {
        field: "final",
        headerName: "Price\nFinal Round 1",
        width: 140,
        editable: userData.is_staff || userData.is_admin ? true : false,
        valueFormatter: (params) => currencyFormatter(params.value, "€"),
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
      },
      {
        field: "customer_abs2",
        headerName: "Price\nCustomer",
        width: 140,

        valueFormatter: (params) => currencyFormatter(params.value, "€"),
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
      }
    );
  } else if (statusH === "1") {
    header.push(
      {
        field: "spot-rate",
        headerName: "Price\nspot-rate",
        width: 140,

        valueFormatter: (params) => currencyFormatter(params.value, "€"),
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
      },
      {
        field: "contract",
        headerName: "Price\nContract",
        width: 140,

        valueFormatter: (params) => currencyFormatter(params.value, "€"),
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
      }
    );
  }
  header.push({
    field: "suggested-price-total",
    headerName: "Suggested Price",
    width: 130,
    valueFormatter: (params) => currencyFormatterInt(params.value, "€"),
    filter: true,
    resizable: true,
    filterParams: filterOptionsPrice,
    sortable: true,
  });
  header.push({
    field: "empty-km",
    headerName: "Empty km",
    width: 100,
    filter: true,
    resizable: true,
    filterParams: filterOptionsPrice,
    sortable: true,
    onCellValueChanged: (params) => {
      const newValue = Number(params.newValue);
      const newSuggestedPrice = Math.round(
        params.data.suggested_price *
          (params.data.distance_here + Number(newValue))
      );
      params.data["suggested-price-total"] = newSuggestedPrice;
      params.api.refreshCells({ columns: ["suggested-price-total"] });
    },
  });
  header.push({
    field: "suggested_price",
    headerName: "€ / km",
    width: 100,
    valueFormatter: (params) => currencyFormatter(params.value, "€"),
    filter: true,
    resizable: true,
    filterParams: filterOptionsPrice,
    sortable: true,
  });

  const capacityText = tenderData?.capacityText?.trim();
  const getCapacityHeaderNameEarly = (type?: string) => {
    switch ((type || '').toLowerCase()) {
      case 'percentage': return 'Capacity (%)';
      case 'daily':
      case 'weekly':
      case 'monthly':
      case 'yearly': return `Capacity (${type?.toLowerCase()})`;
      default: return 'Capacity';
    }
  };
  if ((isAdmin || isViewer) && tender?.[0]?.info_capacity) {
    const earlyCapacityHeaderName = getCapacityHeaderNameEarly(capacityText);
    header.push({
      field: "capacity",
      headerName: earlyCapacityHeaderName,
      filter: true,
      resizable: true,
      filterParams: filterOptionsPrice,
      sortable: true,
      editable: false,
      width: 120,
    });
  }
  const getCapacityHeaderName = (type?: string) => {
    switch ((type || '').toLowerCase()) {
      case 'percentage':
        return 'Capacity (%)';
      case 'daily':
      case 'weekly':
      case 'monthly':
      case 'yearly':
        return `Capacity (${type?.toLowerCase()})`;
      default:
        return 'Capacity';
    }
  };
  const capacityHeaderName = getCapacityHeaderName(capacityText);
  const currentTender = tender?.[0];

  if (currentTender) {

    if (currentTender.transit_time_required && !isAdmin && !isViewer) {
      const transitTimeType = currentTender.transit_time_type || 'days';
      const getTransitTimeHeaderName = (type: string) => {
        switch (type) {
          case 'days':
            return 'Transit Time (days)';
          case 'intervals':
            return 'Transit Time (intervals)';
          case 'hours':
            return 'Transit Time (hours)';
          default:
            return 'Transit Time';
        }
      };

      const getTransitTimeValidation = (type: string) => {
        switch (type) {
          case 'days':
          case 'hours':
            return {
              cellEditor: NumericCellEditor,
              valueParser: (params: any) => {
                // Handle empty values as null for proper deletion
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  return null;
                }
                const numValue = parseFloat(params.newValue);
                return isNaN(numValue) ? null : numValue;
              },
              valueSetter: (params: any) => {
                // Handle empty values as null for proper deletion
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  params.data[params.colDef.field] = null;
                  return true;
                }
                const numValue = parseFloat(params.newValue);
                if (isNaN(numValue) || numValue < 0) {
                  return false; // Reject invalid input
                }
                params.data[params.colDef.field] = numValue;
                return true;
              }
            };
          case 'intervals':
            return {
              cellEditor: 'agTextCellEditor',
              valueParser: (params: any) => {
                // Pass through the raw string so valueSetter can reject and trigger onCellEditingStopped
                if (params.newValue === null || params.newValue === undefined) {
                  return null;
                }
                if (typeof params.newValue !== 'string') {
                  return null;
                }
                const trimmed = params.newValue.trim();
                return trimmed === '' ? null : trimmed;
              },
              valueSetter: (params: any) => {
                // Allow empty values for deletion
                if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
                  params.data[params.colDef.field] = null;
                  return true;
                }
                if (typeof params.newValue !== 'string') {
                  return false;
                }
                // Only accept single-letter dash single-letter format (e.g. A-B, A-C)
                const intervalPattern = /^[A-Za-z]-[A-Za-z]$/;
                const trimmedValue = params.newValue.trim();
                if (!intervalPattern.test(trimmedValue)) {
                  return false; // Reject invalid interval format
                }
                params.data[params.colDef.field] = trimmedValue.toUpperCase();
                return true;
              }
            };
          default:
            return {};
        }
      };

      header.push({
        field: "transit_time",
        headerName: getTransitTimeHeaderName(transitTimeType),
        width: 150,
        minWidth: 140,
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: true,
        transitTimeType: transitTimeType,
        ...getTransitTimeValidation(transitTimeType),
      });
    }
    if (currentTender.additional_costs_required && !isAdmin && !isViewer) {
      const additionalCostsType = currentTender.additional_costs_type || 'absolute';
      const getAdditionalCostsHeaderName = (type: string) => {
        switch (type) {
          case 'percent':
            return 'Additional Costs (%)';
          case 'absolute':
            return 'Additional Costs (€)';
          default:
            return 'Additional Costs';
        }
      };

      const getAdditionalCostsValidation = (type: string) => {
        switch (type) {
          case 'percent':
            return {
              cellEditor: NumericCellEditor,
              valueParser: (params: any) => {
                // Handle empty values as null for proper deletion
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  return null;
                }
                const numValue = parseFloat(params.newValue);
                return isNaN(numValue) ? null : numValue;
              },
              valueSetter: (params: any) => {
                // Handle empty values as null for proper deletion
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  params.data[params.colDef.field] = null;
                  return true;
                }
                const numValue = parseFloat(params.newValue);
                if (isNaN(numValue) || numValue < 0 || numValue > 100) {
                  return false; // Reject invalid input for percentage (0-100)
                }
                params.data[params.colDef.field] = numValue;
                return true;
              },
              valueFormatter: (params) => params.value != null ? `${Number(params.value).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%` : ''
            };
          case 'absolute':
            return {
              cellEditor: NumericCellEditor,
              valueParser: (params: any) => {
                // Handle empty values as null for proper deletion
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  return null;
                }
                const numValue = parseFloat(params.newValue);
                return isNaN(numValue) ? null : numValue;
              },
              valueSetter: (params: any) => {
                // Handle empty values as null for proper deletion
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  params.data[params.colDef.field] = null;
                  return true;
                }
                const numValue = parseFloat(params.newValue);
                if (isNaN(numValue) || numValue < 0) {
                  return false; // Reject invalid input
                }
                params.data[params.colDef.field] = numValue;
                return true;
              },
              valueFormatter: (params) => currencyFormatter(params.value, "€")
            };
          default:
            return {};
        }
      };

      header.push({
        field: "additional_costs",
        headerName: getAdditionalCostsHeaderName(additionalCostsType),
        width: 150,
        minWidth: 140,
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: true,
        ...getAdditionalCostsValidation(additionalCostsType),
      });
    }

    // Note: Margin columns are now added per department above when isAdmin is true
    // For non-admin users, we still show a single margin column
    if (!isAdmin && !isViewer) {
      header.push({
        field: "margin",
        headerName: "Margin",
        width: 100,
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: true,
        cellEditor: NumericCellEditor,
        valueParser: (params) => {
          if (params.newValue === '' || params.newValue === undefined || params.newValue === null) {
            return null;
          }
          const stripped = String(params.newValue).replace(/%/g, '').trim();
          const num = parseFloat(stripped);
          return isNaN(num) ? null : num;
        },
        valueSetter: (params) => {
          if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
            params.data[params.colDef.field] = null;
            return true;
          }
          const num = Number(params.newValue);
          if (isNaN(num) || num < 0 || num > 100) {
            return false;
          }
          params.data[params.colDef.field] = num;
          return true;
        },
        cellStyle: (params) =>
          params.value != null && Number(params.value) > 100
            ? { backgroundColor: '#fef2f2', color: '#dc2626' }
            : null,
        valueFormatter: (params) => params.value != null && params.value !== '' ? `${Number(params.value).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%` : '',
      });
    }
  }
  if (!isAdmin && !isViewer && currentTender) {
    if (currentTender.capacity_required) {
      header.push({
        field: "capacity",
        headerName: capacityHeaderName,
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: true,
        width: 120,
        ...(capacityText?.toLowerCase() === 'percentage' && {
          valueParser: (params) => {
            if (params.newValue === '' || params.newValue === undefined || params.newValue === null) {
              return null;
            }
            const stripped = String(params.newValue).replace(/%/g, '').trim();
            const num = parseFloat(stripped);
            return isNaN(num) ? NaN : num;
          },
          valueSetter: (params) => {
            if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
              params.data[params.colDef.field] = null;
              return true;
            }
            const num = Number(params.newValue);
            if (isNaN(num) || num < 0 || num > 100) {
              return false;
            }
            params.data[params.colDef.field] = num;
            return true;
          },
          valueFormatter: (params) =>
            params.value != null && params.value !== ''
              ? `${String(params.value).replace('.', ',')}%`
              : '',
        }),
      });
    }
    if (statusH === "1") {
      header.push({
        field: "regional",
        headerName: "Price\nRegional",
        valueFormatter: (params) => params.value != null ? `${Number(params.value).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}€` : '',
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: true,
        width: 120,
        cellEditor: NumericCellEditor,
        valueParser: (params) => {
          if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
            return null;
          }
          const numValue = parseFloat(params.newValue);
          return isNaN(numValue) ? null : numValue;
        },
        valueSetter: (params) => {
          if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
            params.data[params.colDef.field] = null;
            return true;
          }
          const numValue = parseFloat(params.newValue);
          if (isNaN(numValue) || numValue < 0) {
            return false;
          }
          params.data[params.colDef.field] = numValue;
          return true;
        },
      });
    } else if (statusH === "2" || statusH === "3") {
      header.push({
        field: "regional-" + statusH,
        headerName: "Price\nRegional Round " + statusH,
        valueFormatter: (params) => params.value != null ? `${Number(params.value).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}€` : '',
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: true,
        width: 120,
        cellEditor: NumericCellEditor,
        valueParser: (params) => {
          if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
            return null;
          }
          const numValue = parseFloat(params.newValue);
          return isNaN(numValue) ? null : numValue;
        },
        valueSetter: (params) => {
          if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
            params.data[params.colDef.field] = null;
            return true;
          }
          const numValue = parseFloat(params.newValue);
          if (isNaN(numValue) || numValue < 0) {
            return false;
          }
          params.data[params.colDef.field] = numValue;
          return true;
        },
      });
    }
    if (currentTender.additional_data_required) {
      header.push({
        field: "additionalData_y",
        headerName: "Additional Data",
        width: 100,
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: true,
      });
    }
    
    // Add required custom columns for department users
    if (customColumns && Array.isArray(customColumns) && departments && departments.length > 0) {
      customColumns
        .filter(col => typeof col === 'object' && col?.required)
        .forEach(customCol => {
          // For department users, add one column for their department
          // Use the first (and typically only) department for the user
          const userDepartment = departments[0];
          const fieldName = `${customCol.column_ref || customCol.name}_${userDepartment.name}`;
          
          header.push({
            field: fieldName,
            headerName: `${customCol.name.replace('custom_', '').replace(/^\d+_/, '')}\n${userDepartment.name}`,
            filter: true,
            resizable: true,
            filterParams: filterOptionsPrice,
            sortable: true,
            editable: true,
          });
        });
    }
  } else {
    departments?.forEach((value) => {
      const departmentViewInfo = departmentViews[value.name];
      const hasViewed = Array.isArray(departmentViewInfo)
        ? departmentViewInfo.length > 0
        : Boolean(departmentViewInfo?.has_viewed);
      const hasRejected = Array.isArray(departmentViewInfo)
        ? false
        : Boolean(departmentViewInfo?.has_rejected);
      const headerClass = hasRejected
        ? "department-rejected"
        : hasViewed
          ? "department-viewed"
          : "";

      if (statusH !== "1" && statusH !== "R") {
        header.push({
          field: `regional-${statusH}` + " - " + value.name, //+ "round" + statusH,
          headerName: "Price\n" + `Round ${statusH} ` + value.name,
          valueFormatter: (params) => params.value != null ? `${Number(params.value).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}€` : '',
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
          headerClass: headerClass,
        });
      } else if (statusH === "1") {
        header.push({
          field: "regional - " + value.name, //+ "round" + statusH,
          headerName: "Regional Price\n" + value.name,
          valueFormatter: (params) => params.value != null ? `${Number(params.value).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}€` : '',
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
          editable: true,
          headerClass: headerClass,
          cellEditor: NumericCellEditor,
          valueParser: (params) => {
            if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
              return null;
            }
            const numValue = parseFloat(params.newValue);
            return isNaN(numValue) ? null : numValue;
          },
          valueSetter: (params) => {
            if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
              params.data[params.colDef.field] = null;
              return true;
            }
            const numValue = parseFloat(params.newValue);
            if (isNaN(numValue) || numValue < 0) {
              return false;
            }
            params.data[params.colDef.field] = numValue;
            return true;
          },
        });
      }

      if (tender[0].capacity_required) {
        header.push({
          field: "capacity - " + value.name, //+ "round" + statusH,
          headerName: "Regional Capacity\n" + value.name,
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
          editable: true,
          headerClass: headerClass,
          ...(capacityText?.toLowerCase() === 'percentage' && {
            valueFormatter: (params) =>
              params.value != null && params.value !== '' ? `${params.value}%` : '',
          }),
        });
      }

      // Only add additional data column if required
      if (tender[0].additional_data_required) {
        header.push({
          field: "additionalData_y_" + value.name, //+ "round" + statusH,
          headerName: "Regional Data\n" + value.name,
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
          editable: true,
          headerClass: headerClass,
        });
      }
      
      // Only add transit time column if required
      if (tender[0].transit_time_required) {
        header.push({
          field: "transitTime_" + value.name, //+ "round" + statusH,
          headerName: (() => {
            const transitTimeType = tender?.[0]?.transit_time_type || 'days';
            const typeLabel = transitTimeType === 'days' ? '(days)' : 
                             transitTimeType === 'intervals' ? '(intervals)' : '(hours)';
            return `Transit Time ${typeLabel}\n${value.name}`;
          })(),
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
          editable: true,
          headerClass: headerClass,
          ...((() => {
            const transitTimeType = tender?.[0]?.transit_time_type || 'days';
            switch (transitTimeType) {
              case 'days':
              case 'hours':
                return {
                  cellEditor: NumericCellEditor,
                  valueParser: (params: any) => {
                    // Handle empty values as null for proper deletion
                    if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                      return null;
                    }
                    const numValue = parseFloat(params.newValue);
                    return isNaN(numValue) ? null : numValue;
                  },
                  valueSetter: (params: any) => {
                    // Handle empty values as null for proper deletion
                    if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                      params.data[params.colDef.field] = null;
                      return true;
                    }
                    const numValue = parseFloat(params.newValue);
                    if (isNaN(numValue) || numValue < 0) {
                      return false;
                    }
                    params.data[params.colDef.field] = numValue;
                    return true;
                  }
                };
              case 'intervals':
                return {
                  cellEditor: 'agTextCellEditor',
                  valueParser: (params: any) => {
                    // Pass through the raw string so valueSetter can reject and trigger onCellEditingStopped
                    if (params.newValue === null || params.newValue === undefined) {
                      return null;
                    }
                    if (typeof params.newValue !== 'string') {
                      return null;
                    }
                    const trimmed = params.newValue.trim();
                    return trimmed === '' ? null : trimmed;
                  },
                  valueSetter: (params: any) => {
                    // Allow empty values for deletion
                    if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
                      params.data[params.colDef.field] = null;
                      return true;
                    }
                    if (typeof params.newValue !== 'string') {
                      return false;
                    }
                    // Only accept single-letter dash single-letter format (e.g. A-B, A-C)
                    const intervalPattern = /^[A-Za-z]-[A-Za-z]$/;
                    const trimmedValue = params.newValue.trim();
                    if (!intervalPattern.test(trimmedValue)) {
                      return false;
                    }
                    params.data[params.colDef.field] = trimmedValue.toUpperCase();
                    return true;
                  }
                };
              default:
                return {};
            }
          })()),
          transitTimeType: (() => {
            return tender?.[0]?.transit_time_type || 'days';
          })(),
        });
      }
      
      // Only add additional costs column if required
      if (tender[0].additional_costs_required) {
        const additionalCostsType = tender?.[0]?.additional_costs_type || 'absolute';
        const getAdditionalCostsHeaderName = (type: string, departmentName: string) => {
          const typeLabel = type === 'percent' ? '(%)' : '(€)';
          return `Additional Costs ${typeLabel}\n${departmentName}`;
        };
        
        const getAdditionalCostsConfig = (type: string) => {
          if (type === 'percent') {
            return {
              valueFormatter: (params) => params.value != null ? `${Number(params.value).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%` : '',
              cellEditor: NumericCellEditor,
              valueParser: (params: any) => {
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  return null;
                }
                const numValue = parseFloat(params.newValue);
                return isNaN(numValue) ? null : numValue;
              },
              valueSetter: (params: any) => {
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  params.data[params.colDef.field] = null;
                  return true;
                }
                const numValue = parseFloat(params.newValue);
                if (isNaN(numValue) || numValue < 0 || numValue > 100) {
                  return false;
                }
                params.data[params.colDef.field] = numValue;
                return true;
              }
            };
          } else {
            return {
              valueFormatter: (params) => currencyFormatter(params.value, "€"),
              cellEditor: NumericCellEditor,
              valueParser: (params: any) => {
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  return null;
                }
                const numValue = parseFloat(params.newValue);
                return isNaN(numValue) ? null : numValue;
              },
              valueSetter: (params: any) => {
                if (params.newValue === "" || params.newValue === undefined || params.newValue === null) {
                  params.data[params.colDef.field] = null;
                  return true;
                }
                const numValue = parseFloat(params.newValue);
                if (isNaN(numValue) || numValue < 0) {
                  return false;
                }
                params.data[params.colDef.field] = numValue;
                return true;
              }
            };
          }
        };

        header.push({
          field: "additional_costs_" + value.name, //+ "round" + statusH,
          headerName: getAdditionalCostsHeaderName(additionalCostsType, value.name),
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
          editable: true,
          headerClass: headerClass,
          ...getAdditionalCostsConfig(additionalCostsType),
        });
      }

      // Add required custom columns per department
      if (customColumns && Array.isArray(customColumns)) {
        customColumns
          .filter(col => typeof col === 'object' && col?.required)
          .forEach(customCol => {
            // Field name should be: customColumnRef_departmentName
            const fieldName = `${customCol.column_ref || customCol.name}_${value.name}`;
            
            header.push({
              field: fieldName,
              headerName: `${customCol.name.replace('custom_', '').replace(/^\d+_/, '')}\n${value.name}`,
              filter: true,
              resizable: true,
              filterParams: filterOptionsPrice,
              sortable: true,
              editable: true,
              headerClass: headerClass,
            });
          });
      }

      // Add margin column per department
      header.push({
        field: "margin - " + value.name,
        headerName: "Margin\n" + value.name,
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: true,
        width: 100,
        headerClass: headerClass,
        cellEditor: NumericCellEditor,
        valueParser: (params) => {
          if (params.newValue === '' || params.newValue === undefined || params.newValue === null) {
            return null;
          }
          const stripped = String(params.newValue).replace(/%/g, '').trim();
          const num = parseFloat(stripped);
          return isNaN(num) ? null : num;
        },
        valueSetter: (params) => {
          if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
            params.data[params.colDef.field] = null;
            return true;
          }
          const num = Number(params.newValue);
          if (isNaN(num) || num < 0 || num > 100) {
            return false;
          }
          params.data[params.colDef.field] = num;
          return true;
        },
        cellStyle: (params) =>
          params.value != null && Number(params.value) > 100
            ? { backgroundColor: '#fef2f2', color: '#dc2626' }
            : null,
        valueFormatter: (params) => params.value != null && params.value !== '' ? `${Number(params.value).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%` : '',
      });
    });
    if (statusH !== "1" && statusH !== "R") {
      header.push({
        field: "round" + statusH,
        headerName: "Price\nFinal Round " + statusH,
        valueFormatter: (params) => currencyFormatter(params.value, "€"),
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
      });
    } else if (statusH === "1") {
      header.push({
        field: "final",
        headerName: "Price\nFinal",
        valueFormatter: (params) => currencyFormatter(params.value, "€"),
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
        editable: userData.is_staff || userData.is_admin ? true : false,
      });
    }
    if (statusH === "R") {
      departments?.forEach((value) => {
        header.push({
          field: "regional" + value["username"],
          headerName: "Regional Price\n" + value["locationInfo"] + " Round 1",
          valueFormatter: (params) => currencyFormatterInt(params.value, "€"),
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
        });
      });
      header.push(
        {
          field: "final",
          headerName: "Price\nFinal Round 1",
          valueFormatter: (params) => currencyFormatter(params.value, "€"),
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
          editable: userData.is_staff || userData.is_admin ? true : false,
        },
        {
          field: "customer_abs2",
          headerName: "Customer Price\n Round 1",
          valueFormatter: (params) => currencyFormatter(params.value, "€"),
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
        }
      );
      departments.forEach((value) => {
        header.push({
          field: "regional" + value["username"] + "round2",
          headerName: "Regional Price\n" + value["locationInfo"] + " Round 2",
          valueFormatter: (params) => currencyFormatterInt(params.value, "€"),
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
        });
      });
      header.push(
        {
          field: "round2",
          headerName: "Price\nFinal Round 2",
          valueFormatter: (params) => currencyFormatter(params.value, "€"),
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
        },
        {
          field: "customer_abs3",
          headerName: "Customer Price\n Round 2",
          valueFormatter: (params) => currencyFormatter(params.value, "€"),
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
        }
      );
      departments.forEach((value) => {
        header.push({
          field: "regional" + value["username"] + "round3",
          headerName: "Regional Price\n" + value["locationInfo"] + " Round 3",
          valueFormatter: (params) => currencyFormatterInt(params.value, "€"),
          filter: true,
          resizable: true,
          filterParams: filterOptionsPrice,
          sortable: true,
        });
      });
      header.push({
        field: "round3",
        headerName: "Price\nFinal Round 3",
        valueFormatter: (params) => currencyFormatter(params.value, "€"),
        filter: true,
        resizable: true,
        filterParams: filterOptionsPrice,
        sortable: true,
      });
    }
  }

  // Add Missing column for department users (not admin or viewer)
  if (!isAdmin && !isViewer) {
    header.push({
      field: "missing",
      headerName: "Missing",
      filter: true,
      resizable: true,
      sortable: true,
      editable: false,
      width: 150,
      cellStyle: (params) => {
        if (params.value && params.value.trim() !== '') {
          return { 
            backgroundColor: '#ffebee',
            border: '1px solid #f44336'
          };
        }
        return null;
      },
      tooltipField: "missing"
    });
  }

  // Add address quality score columns
    header.push(
      {
        field: "origin_address_quality",
        headerName: "Origin\nAddress Quality",
        width: 140,
        filter: true,
        resizable: true,
        sortable: true,
        valueFormatter: (params) => {
          if (params.value == null) return '-';
          const score = parseFloat(params.value);
          return score.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
        },
        cellStyle: (params) => {
          if (params.value == null) return null;
          const score = parseFloat(params.value);
          if (score < 70) {
            return { backgroundColor: '#ffebee', color: '#c62828' }; // Red for low quality
          } else if (score < 85) {
            return { backgroundColor: '#fff3e0', color: '#e65100' }; // Orange for medium quality
          }
          return { backgroundColor: '#e8f5e9', color: '#2e7d32' }; // Green for good quality
        },
        tooltipValueGetter: (params) => {
          if (params.value == null) return 'Address quality score not available';
          const score = parseFloat(params.value);
          if (score < 70) {
            return `Low quality geocoding (${score.toFixed(1)}%) - Route calculation may be inaccurate`;
          } else if (score < 85) {
            return `Medium quality geocoding (${score.toFixed(1)}%) - Verify address accuracy`;
          }
          return `Good quality geocoding (${score.toFixed(1)}%)`;
        }
      },
      {
        field: "destination_address_quality",
        headerName: "Destination\nAddress Quality",
        width: 140,
        filter: true,
        resizable: true,
        sortable: true,
        valueFormatter: (params) => {
          if (params.value == null) return '-';
          const score = parseFloat(params.value);
          return score.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
        },
        cellStyle: (params) => {
          if (params.value == null) return null;
          const score = parseFloat(params.value);
          if (score < 70) {
            return { backgroundColor: '#ffebee', color: '#c62828' }; // Red for low quality
          } else if (score < 85) {
            return { backgroundColor: '#fff3e0', color: '#e65100' }; // Orange for medium quality
          }
          return { backgroundColor: '#e8f5e9', color: '#2e7d32' }; // Green for good quality
        },
        tooltipValueGetter: (params) => {
          if (params.value == null) return 'Address quality score not available';
          const score = parseFloat(params.value);
          if (score < 70) {
            return `Low quality geocoding (${score.toFixed(1)}%) - Route calculation may be inaccurate`;
          } else if (score < 85) {
            return `Medium quality geocoding (${score.toFixed(1)}%) - Verify address accuracy`;
          }
          return `Good quality geocoding (${score.toFixed(1)}%)`;
        }
      }
    );

  const setPricedButtonConfig = {
    field: "copy-price",
    headerName: "Set suggested price",
    filter: false,
    resizable: true,
    sortable: false,
    width: 150,
    valueGetter: () => {
      return "Set";
    },
    cellClass: "poly-view",
  };

  // Only add the click handler if the user is not a viewer
  if (!isViewer) {
    setPricedButtonConfig.onCellClicked = (params) => {
      const oldValue = params.data["regional"];
      // Always round the suggested price value before setting
      const roundedValue = Math.round(params.data["suggested-price-total"]);
      params.data["regional"] = roundedValue;
      params.api.refreshCells({ columns: ["regional"] });
      const colDef = params.api.getColumnDef("regional"); // Get the column definition
      params.api.dispatchEvent({
        type: "cellValueChanged",
        data: params.data,
        node: params.node,
        oldValue: oldValue,
        newValue: roundedValue,
        colDef: colDef,
        column: params.column,
        rowIndex: params.rowIndex,
        api: params.api,
        columnApi: params.columnApi,
        context: params.api.context,
        rowPinned: false,
      });
    };
  }

  if (!isViewer) {
  header.push(setPricedButtonConfig);
  }

  header.push({
    field: "map",
    headerName: "Route",
    filter: false,
    resizable: true,
    sortable: false,
    width: 120,
    valueGetter: () => {
      return "View";
    },
    onCellClicked: (e) => {
      fetchMap(e.data.id);
    },
    cellClass: "poly-view",
  });
  header[header.length - 1]["editable"] = !isOverview;

  return applySavedColumnLayout(header, getColumnLayoutScope(tenderID));
}

export const setGridColsFeedback = async (tenderID) => {
  let statusH = (await axios.get(API_URL_STATUS + tenderID)).data;
  let laneColumnDefinitionRound2c: any = [
    {
      field: "originCountry",
      headerName: "Origin\nCountry",
      filter: true,
      resizable: true,
    },
    {
      field: "originZIP",
      headerName: "Origin\nZIP",
      filter: true,
      resizable: true,
    },
    {
      field: "destinationCountry",
      headerName: "Destination\nCountry",
      filter: true,
      resizable: true,
    },
    {
      field: "destinationZIP",
      headerName: "Destination\nZIP",
      filter: true,
      resizable: true,
    },
    {
      field: "volume",
      headerName: "Volume",
    },
    {
      field: "tolls",
      headerName: "Tolls",
      valueFormatter: (params) => currencyFormatter(params.value, "€"),
      filter: "agNumberColumnFilter",
      filterParams: {
        suppressAndOrCondition: true,
        filterOptions: ["greaterThan"],
      },
    },
    {
      field: "distance_here",
      headerName: "Distance",
      valueFormatter: (params) => distanceFormatter(params.value, "km"),
      filter: "agNumberColumnFilter",
      filterParams: {
        suppressAndOrCondition: true,
        filterOptions: ["greaterThan"],
      },
    },
  ];
  if (statusH === "2") {
    laneColumnDefinitionRound2c.push({
      field: "final",
      headerName: "Price\nFinal Round 1",
      valueFormatter: (params) => currencyFormatter(params.value, "€"),
      filter: "agNumberColumnFilter",
      filterParams: {
        suppressAndOrCondition: true,
        filterOptions: ["greaterThan"],
      },
    });
  } else {
    laneColumnDefinitionRound2c.push({
      field: "round2",
      headerName: "Price\nFinal Round 2",
      valueFormatter: (params) => currencyFormatter(params.value, "€"),
      filter: "agNumberColumnFilter",
      filterParams: {
        suppressAndOrCondition: true,
        filterOptions: ["greaterThan"],
      },
    });
  }
  laneColumnDefinitionRound2c.push(
    {
      field: "customer_abs" + statusH,
      headerName: "Price\nCustomer in €",
      valueFormatter: (params) => currencyFormatter(params.value, "€"),
      filter: "agNumberColumnFilter",
      filterParams: {
        suppressAndOrCondition: true,
        filterOptions: ["greaterThan"],
      },
      editable: true,
    },
    {
      wrapText: true,
      field: "customer_rel" + statusH,
      headerName: "Price\nCustomer in %",
      valueFormatter: (params) => percentFormatter(params.value, "%"),
      filter: "agNumberColumnFilter",
      filterParams: {
        suppressAndOrCondition: true,
        filterOptions: ["greaterThan"],
      },
      editable: true,
    }
  );

  return laneColumnDefinitionRound2c;
};
